import { useState } from "react";
import { AppShell, Avatar, Badge, Box, Burger, Button, CloseButton, Group, Menu, NavLink, ScrollArea, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { IconAdjustmentsHorizontal, IconArrowRight, IconBook, IconCode, IconCpu, IconExternalLink, IconHome, IconKey, IconLock, IconLogout, IconPackage, IconSparkles } from "@tabler/icons-react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { LANGUAGES } from "@passio/api-client";
import { brand } from "../theme/brand";
import { useNav } from "../lib/nav";
import { useSession } from "../lib/session";
import { closeClientSettings, openClientSettings, useClientHeaders, useSettingsDrawer } from "../lib/clientSettings";
import { useRouteModels } from "../lib/models";
import { openInspector } from "../lib/calls";
import { InspectorDrawer, LastCallBar } from "./Inspector";
import { SettingsDrawer } from "./SettingsDrawer";
import { DocsMenu } from "./DocsMenu";
import { UsageMeter } from "./common";
import { accountsLink } from "../lib/track";
import { ACCOUNT_CTA, AccountButton } from "./AccountCta";

const EXPIRY_WARNING_MS = 3 * 86_400_000;

export function Logo({ dark = true }: { dark?: boolean }) {
  return (
    <Group gap={8} wrap="nowrap">
      <Text fw={800} fz={22} c={dark ? "white" : "ink.8"} style={{ letterSpacing: "-0.03em" }}>
        {brand.name}
      </Text>
      <Text fz={12} fw={600} c="lime.5" ff="monospace" mt={4}>
        {brand.product}
      </Text>
    </Group>
  );
}

function SettingsSummary() {
  const h = useClientHeaders();
  const lang = LANGUAGES.find((l) => l.value === (h.localizationIso ?? "en"))?.label ?? h.localizationIso;
  const filters = Object.keys(h.searchConfig.toJSON()).length;
  const models = Object.keys(useRouteModels()).length;
  return (
    <Text fz={11} c="var(--pd-ink-muted)" ff="monospace" visibleFrom="md">
      {lang}
      {h.productLocalization ? ` · ${h.productLocalization}` : ""}
      {filters ? ` · ${filters} filter${filters > 1 ? "s" : ""}` : ""}
      {models ? ` · ${models} model${models > 1 ? "s" : ""}` : ""}
    </Text>
  );
}

const BUILD_LINKS = [
  { to: "/guide", label: "API guide", description: "Headers, request IDs, feedback, errors", icon: IconBook },
  { to: "/models", label: "Models", description: "Compare speed, accuracy and cost", icon: IconCpu },
  { to: "/developers", label: "Client SDKs", description: "Install the typed clients this demo uses", icon: IconPackage },
];

const STUDIO_LINK = { to: "/studio", label: "Easy AI Studio", description: "Build your own AI endpoint", icon: IconSparkles };

function StudioCard({ active, onClick, onDismiss }: { active: boolean; onClick: () => void; onDismiss: () => void }) {
  return (
    <Box pos="relative">
      <UnstyledButton component={Link} to="/studio" onClick={onClick} className="pd-dark" p="sm" pr={36} style={{ display: "block", borderRadius: 12, border: `1px solid ${active ? "var(--pd-lime)" : "var(--pd-ink-border)"}` }}>
        <Group gap={8} wrap="nowrap" mb={4}>
          <IconSparkles size={16} color="var(--pd-lime)" />
          <Text fz="sm" fw={700} c="white">
            Don't see what you need?
          </Text>
        </Group>
        <Text fz={12} className="pd-dim" lh={1.4}>
          Build your own AI endpoint in Easy AI Studio. NutritionAI includes $10 of Studio usage free every month.
        </Text>
        <Group gap={4} mt={6}>
          <Text fz={12} fw={600} c="lime.5">
            See what you can build
          </Text>
          <IconArrowRight size={12} color="var(--pd-lime)" />
        </Group>
      </UnstyledButton>
      <CloseButton pos="absolute" top={8} right={8} size="sm" variant="transparent" c="gray.5" onClick={onDismiss} aria-label="Hide this card" title="Hide (Easy AI Studio stays under Build)" />
    </Box>
  );
}

const STRIP_DISMISSED = "passio-demo.strip-dismissed";

/** Top-of-page reminder for demo accounts; can be hidden for the session until the last few days. */
function DemoStrip({ endsAt }: { endsAt: Date | null }) {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(STRIP_DISMISSED) === "1");
  const msLeft = endsAt ? endsAt.getTime() - Date.now() : null;
  const urgent = msLeft !== null && msLeft < EXPIRY_WARNING_MS;
  if (dismissed && !urgent) return null;
  const days = msLeft !== null ? Math.max(0, Math.ceil(msLeft / 86_400_000)) : null;
  const left = days === null ? null : days <= 1 ? "less than a day" : `${days} days`;
  return (
    <Group
      mb="md"
      px="md"
      py="xs"
      justify="space-between"
      gap="sm"
      wrap="wrap"
      className="pd-dark"
      style={{ borderRadius: 12, border: `1px solid ${urgent ? "var(--pd-amber)" : "var(--pd-lime)"}` }}
    >
      <Text fz="sm" c="white">
        <b>{urgent ? `Your demo ends in ${left}.` : left ? `Free demo · ${left} left.` : "Free demo account."}</b>{" "}
        <span className="pd-dim">Create your Passio account to get your own API key and keep building.</span>
      </Text>
      <Group gap="xs" wrap="nowrap">
        <AccountButton placement={urgent ? "expiring" : "banner"} size="xs">
          Create account
        </AccountButton>
        {!urgent && (
          <CloseButton
            size="sm"
            variant="transparent"
            c="var(--pd-ink-muted)"
            aria-label="Hide for now"
            onClick={() => {
              sessionStorage.setItem(STRIP_DISMISSED, "1");
              setDismissed(true);
            }}
          />
        )}
      </Group>
    </Group>
  );
}

export function AppLayout() {
  const [navOpen, nav] = useDisclosure(false);
  const [studioCardHidden, setStudioCardHidden] = useLocalStorage({ key: "passio-demo.studio-card-hidden", defaultValue: false, getInitialValueInEffect: false });
  const settingsOpen = useSettingsDrawer();
  const { me, logout, hasFeature } = useSession();
  const loc = useLocation();
  const navSections = useNav();
  const name = me?.user.displayName || me?.user.username || "";
  // API-key sign-ins already have a Passio account.
  const prospect = me?.user.authKind !== "api_key";
  const endsAt = me?.user.accessExpiresAt ? new Date(me.user.accessExpiresAt) : null;

  return (
    <AppShell header={{ height: 64 }} navbar={{ width: 272, breakpoint: "sm", collapsed: { mobile: !navOpen } }} padding="lg">
      <AppShell.Header className="pd-dark" style={{ borderBottom: "1px solid var(--pd-ink-border)" }}>
        <Group h="100%" px="lg" justify="space-between" wrap="nowrap">
          <Group gap="md" wrap="nowrap">
            <Burger opened={navOpen} onClick={nav.toggle} hiddenFrom="sm" size="sm" color="white" />
            <Link to="/" style={{ textDecoration: "none" }}>
              <Logo />
            </Link>
            {me?.app.environmentLabel && (
              <Badge color="violet" variant="light" size="sm" style={{ flexShrink: 0 }}>
                {me.app.environmentLabel}
              </Badge>
            )}
            {me?.user.authKind === "api_key" && (
              <Tooltip label={`Calls run on your own Passio account${me.user.customerId ? ` (customer ${me.user.customerId})` : ""} via ${me.app.apiHost}, with your plan's limits and billing.`} multiline w={300}>
                <Badge color="lime" variant="light" size="sm" leftSection={<IconKey size={12} />} visibleFrom="lg" style={{ flexShrink: 0 }}>
                  Your API key
                </Badge>
              </Tooltip>
            )}
          </Group>
          <Group gap="sm" wrap="nowrap">
            <Box visibleFrom="md" mr="sm">
              <UsageMeter dark />
            </Box>
            {prospect ? (
              <AccountButton placement="header" radius="md" rightSection={null} style={{ flexShrink: 0 }}>
                <Text component="span" fz="sm" fw={700} visibleFrom="lg">
                  {ACCOUNT_CTA}
                </Text>
                <Text component="span" fz="sm" fw={700} hiddenFrom="lg">
                  Sign up
                </Text>
              </AccountButton>
            ) : (
              <Button component="a" {...accountsLink("header")} variant="outline" color="gray.0" radius="md" visibleFrom="md" styles={{ root: { borderColor: "var(--pd-ink-border)" } }}>
                Your Passio account
              </Button>
            )}
            <Tooltip label="Headers sent with every call">
              <Button variant="outline" color="gray.0" radius="md" leftSection={<IconAdjustmentsHorizontal size={16} />} onClick={openClientSettings} aria-label="Client settings" styles={{ root: { borderColor: "var(--pd-ink-border)" }, section: { margin: 0 } }}>
                <Stack gap={0} align="flex-start" visibleFrom="md" ml={8}>
                  <Text fz="sm" fw={600} lh={1.2}>
                    Client settings
                  </Text>
                  <SettingsSummary />
                </Stack>
              </Button>
            </Tooltip>
            <DocsMenu />
            <Tooltip label="Last call inspector">
              <Button variant="subtle" color="gray.0" px={10} onClick={() => openInspector(null)} aria-label="Open inspector">
                <IconCode size={18} />
              </Button>
            </Tooltip>
            <Menu position="bottom-end" width={240}>
              <Menu.Target>
                <UnstyledButton aria-label="Account">
                  <Avatar color="lime" radius="xl" size={34}>
                    {me?.user.authKind === "api_key" ? <IconKey size={18} /> : name.slice(0, 2).toUpperCase()}
                  </Avatar>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>
                  {name}
                  {me?.user.company ? ` · ${me.user.company}` : ""}
                </Menu.Label>
                {me?.user.authKind === "api_key" && <Menu.Label>Signed in with your API key{me.user.customerId ? ` · customer ${me.user.customerId}` : ""}</Menu.Label>}
                {me?.user.accessExpiresAt && (
                  <Menu.Label>
                    Access until {new Date(me.user.accessExpiresAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                  </Menu.Label>
                )}
                <Menu.Item component="a" {...accountsLink("menu")} leftSection={<IconExternalLink size={16} />}>
                  {prospect ? ACCOUNT_CTA : "Manage your Passio account"}
                </Menu.Item>
                <Menu.Item leftSection={<IconLogout size={16} />} onClick={logout}>
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm" style={{ background: "#fff", borderRight: "1px solid var(--mantine-color-default-border)" }}>
        <AppShell.Section grow component={ScrollArea}>
          <NavLink component={Link} to="/" label="Overview" leftSection={<IconHome size={18} />} active={loc.pathname === "/"} onClick={nav.close} className="pd-nav-link" style={{ borderRadius: 10 }} />
          {navSections.map((section) => (
            <Box key={section.verb} mt="md">
              <Text fz={11} fw={700} c="dimmed" tt="uppercase" px="sm" mb={4} style={{ letterSpacing: "0.06em" }}>
                {section.verb}
              </Text>
              {section.items.map((item) => {
                const locked = !hasFeature(item.feature);
                return (
                  <NavLink
                    key={item.path}
                    component={Link}
                    to={item.path}
                    label={item.label}
                    description={item.description}
                    leftSection={<item.icon size={18} />}
                    rightSection={locked ? <IconLock size={14} color="var(--mantine-color-dimmed)" /> : null}
                    active={loc.pathname.startsWith(item.path)}
                    onClick={nav.close}
                    className="pd-nav-link"
                    style={{ borderRadius: 10, opacity: locked ? 0.55 : 1 }}
                  />
                );
              })}
            </Box>
          ))}
          <Box mt="md">
            <Text fz={11} fw={700} c="dimmed" tt="uppercase" px="sm" mb={4} style={{ letterSpacing: "0.06em" }}>
              Build
            </Text>
            {(studioCardHidden ? [...BUILD_LINKS, STUDIO_LINK] : BUILD_LINKS).map((l) => (
              <NavLink
                key={l.to}
                component={Link}
                to={l.to}
                label={l.label}
                description={l.description}
                leftSection={<l.icon size={18} />}
                active={loc.pathname.startsWith(l.to)}
                onClick={nav.close}
                className="pd-nav-link"
                style={{ borderRadius: 10 }}
              />
            ))}
          </Box>
        </AppShell.Section>
        {!studioCardHidden && (
          <AppShell.Section pt="sm">
            <StudioCard active={loc.pathname.startsWith("/studio")} onClick={nav.close} onDismiss={() => setStudioCardHidden(true)} />
          </AppShell.Section>
        )}
        <AppShell.Section hiddenFrom="md" p="sm">
          <UsageMeter />
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {prospect && <DemoStrip endsAt={endsAt} />}
        {me?.settings.bannerMessage && (
          <Box mb="md" p="sm" style={{ background: "#f2efff", border: "1px solid var(--pd-violet)", borderRadius: 10 }}>
            <Text fz="sm">{me.settings.bannerMessage}</Text>
          </Box>
        )}
        {me && !me.app.nutritionConfigured && (
          <Box mb="md" p="sm" style={{ background: "#fff4e5", border: "1px solid var(--pd-amber)", borderRadius: 10 }}>
            <Text fz="sm">The server has no NUTRITION_API_KEY configured, so Passio calls will fail with 503.</Text>
          </Box>
        )}
        <Box maw={1240} mx="auto" pb={80}>
          <Outlet />
        </Box>
      </AppShell.Main>

      <SettingsDrawer opened={settingsOpen} onClose={closeClientSettings} />
      <InspectorDrawer />
      <LastCallBar />
    </AppShell>
  );
}
